<template>
  <DpplHeaderFooterContent class="right-panel">
    <template #header>
      <div class="px-6 py-4 border-b-sm">
        <h2 class="text-h2">
          {{ currentTab === 'history'
            ? 'Version history'
            : (currentTab === 'similarJobs' ? 'Similar Jobs' : 'Comments')
          }}
        </h2>
      </div>
    </template>

    <RevisionList
      v-if="currentTab === 'history'"
      :revisions="allRevisions"
      @revision-click="handleRevisionClick"
    >
      <template #before-revision-item="{ revision }">
        <div class="score-meter-wrapper flex-shrink-0 d-flex align-center justify-center">
          <ScoreMeter
            :score="revision.score"
            :icon="true"
          />
        </div>
      </template>
    </RevisionList>

    <CommentsSidebar
      v-if="currentTab === 'comments'"
      :page-id="jobId"
      :page-name="route?.name"
      :page-title="title"
    />

    <SimilarJobsContainer
      v-if="currentTab === 'similarJobs'"
    />
  </DpplHeaderFooterContent>

  <DiffModal
    :model-value="showDiffModal"
    :job-id="jobId"
    :total-pages="totalPages"
    :page="page"
    reverse-pages
    :page-ends="{ start: 2, end: totalPages }"
    :disabled="!isEditable"
    @close="handleCloseDiff"
    @next-page="handleNextPage"
    @prev-page="handlePrevPage"
  />
</template>

<script setup>
import { storeToRefs } from 'pinia';
import { ref, computed } from 'vue';
import { useAppRoute } from '@/stores/app/route';
import { useDocumentRightPanel } from '@/stores/document/rightPanel';
import { useEditor } from '@/stores/editor/editor';
import { useEditorDiffModal } from '@/stores/editorDiffModal/editorDiffModal';
import CommentsSidebar from '@/components/document/comments/CommentsSidebar.vue';
import DpplHeaderFooterContent from '@ui/components/general/DpplHeaderFooterContent/DpplHeaderFooterContent.vue';
import DiffModal from '@/components/editor/DiffModal.vue';
import RevisionList from '@ui/components/editor/RevisionList/RevisionList.vue';
import SimilarJobsContainer from '@/components/document/rightPanel/SimilarJobsContainer.vue';
import ScoreMeter from '@/components/editor/ScoreMeter.vue';

const rightPanelStore = useDocumentRightPanel();
const editorDiffModalStore = useEditorDiffModal();

const { route } = storeToRefs(useAppRoute());
const { currentTab } = storeToRefs(rightPanelStore);
const { page } = storeToRefs(editorDiffModalStore);
const { allRevisions, isEditable } = storeToRefs(useEditor());
const { jobId, title } = storeToRefs(useEditor());

const showDiffModal = ref(false);

const totalPages = computed(() => allRevisions.value?.length || 1);

const handleRevisionClick = ({ revision, index }) => {
  showDiffModal.value = true;
  editorDiffModalStore.setPage(index + 1);
  editorDiffModalStore.initDiffModal({
    selectedHistoricalId: revision.id,
    page: index + 1,
    jobId: jobId.value,
    shareKey: window.sessionStorage.shareKey,
  });
};

const handleCloseDiff = () => {
  showDiffModal.value = false;
  editorDiffModalStore.resetDiff();
};

const handleNextPage = () => {
  const newPage = page.value + 1;

  const newPrevRevision = allRevisions.value[newPage - 2];
  const newRevision = allRevisions.value[newPage - 1];
  const newNextRevision = allRevisions.value[newPage];

  const revisionsToSet = {
    jobId: jobId.value,
    selectedHistoricalId: newRevision.id,
    shareKey: window.sessionStorage.shareKey,
  };
  if (newPrevRevision) {
    revisionsToSet.previousHistoricalId = newPrevRevision.id;
  }

  if (newNextRevision) {
    revisionsToSet.nextHistoricalId = newNextRevision.id;
  }

  editorDiffModalStore.setPage(newPage);
  editorDiffModalStore.getHistoricalRevisions(revisionsToSet);
};

const handlePrevPage = () => {
  const hasHistoryOfRevisions = allRevisions.value.length > 1;
  const newPage = page.value === 2 && hasHistoryOfRevisions ? 2 : page.value - 1;

  const newPrevRevision = allRevisions.value[newPage - 2];
  const newRevision = allRevisions.value[newPage - 1];
  const newNextRevision = allRevisions.value[newPage];

  const revisionsToSet = {
    jobId: jobId.value,
    selectedHistoricalId: newRevision.id,
    shareKey: window.sessionStorage.shareKey,
  };

  if (newPrevRevision) {
    revisionsToSet.previousHistoricalId = newPrevRevision.id;
  }

  if (newNextRevision) {
    revisionsToSet.nextHistoricalId = newNextRevision.id;
  }

  editorDiffModalStore.setPage(newPage);
  editorDiffModalStore.getHistoricalRevisions(revisionsToSet);
};
</script>

<style lang="less" scoped>
.score-meter-wrapper {
  width: 40px;
  height: 40px;
}
</style>
